import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ContentLoader,
  IconCheckmark,
  IconDisabled,
  List,
  StatusCard,
} from 'shared/components';
import { displayValue, formatDate } from 'shared/helpers';
import { fetchDevicesByLicenseUser } from '../../licenses/actions';

const LicenseUserDevices = ({ licenseUserId }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  const handleRowClick = (row) => {
    const deviceId = get(row, 'id');
    if (deviceId) {
      history.push(`/devices/${deviceId}`);
    }
  };

  useEffect(() => {
    if (!licenseUserId) return;
    setLoading(true);
    setError(false);
    fetchDevicesByLicenseUser(licenseUserId)
      .then((res) => {
        const payload = get(res, 'data');
        const results = Array.isArray(payload)
          ? payload
          : get(payload, 'results', []);
        setDevices(results);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setDevices([]);
        setLoading(false);
      });
  }, [licenseUserId, retryToken]);

  const handleRetry = () => setRetryToken((token) => token + 1);

  const columns = [
    {
      Header: t('Created on'),
      accessor: 'created_at',
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 140,
    },
    {
      Header: t('Device name'),
      accessor: 'device_name',
      Cell: (cellData) => displayValue(cellData.value),
      width: 200,
    },
    {
      Header: t('Hardware ID'),
      accessor: 'hardware_id',
      Cell: (cellData) => displayValue(cellData.value),
      width: 220,
    },
    {
      Header: t('License'),
      id: 'license',
      accessor: (row) =>
        get(row, 'license.license_key') ??
        get(row, 'license.licenseKey') ??
        null,
      Cell: (cellData) => displayValue(cellData.value),
      width: 220,
    },
    {
      Header: t('Product'),
      accessor: 'license.product.product_name',
      Cell: (cellData) => displayValue(cellData.value),
    },
    {
      Header: t('Activated on'),
      accessor: 'activated_at',
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 140,
    },
    {
      Header: t('Last seen'),
      accessor: 'last_seen_at',
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 140,
    },
    {
      Header: t('Is active'),
      accessor: 'active',
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: (cellData) =>
        cellData.value ? (
          <IconCheckmark color="#10ac84" height="14px" />
        ) : (
          <IconDisabled color="#aaa" height="14px" />
        ),
      width: 90,
    },
    {
      Header: t('Status'),
      id: 'status',
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: (rowData) => {
        const isActive = get(rowData, 'original.active');
        const isBlocked = get(rowData, 'original.blocked');

        if (isBlocked) {
          return <StatusCard status="error" text={t('Blocked')} />;
        }

        return (
          <StatusCard
            status={isActive ? 'success' : 'error'}
            text={isActive ? t('Active') : t('Inactive')}
          />
        );
      },
      width: 110,
    },
  ];

  if (loading) {
    return <ContentLoader text={t('Loading devices')} />;
  }

  if (error) {
    return (
      <div className="LicenseUserDetails-error">
        <p>{t('Unable to load devices for this user')}</p>
        <Button theme="info" size="sm" onClick={handleRetry}>
          {t('Retry')}
        </Button>
      </div>
    );
  }

  if (!devices.length) {
    return (
      <div className="LicenseUserDetails-empty">
        {t('No devices for this user')}
      </div>
    );
  }

  return (
    <div className="LicenseUserDetails-list">
      <List
        data={devices}
        columns={columns}
        minRows={devices.length || 5}
        showPagination
        loading={loading}
        defaultPageSize={20}
        getTrProps={(state, rowInfo) => ({
          onClick: () => handleRowClick(rowInfo.original),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
};

LicenseUserDevices.propTypes = {
  licenseUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default LicenseUserDevices;
